import React from 'react';
import { Box, Card, Chip, HorizontalScroll, IconId, SkeletonLoader, Typography } from '@drone-js/core';
import { connect, useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { getImageUrl } from 'core/utils/activityUrlGenerator';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/search/City/QuickCategoryFilter/QuickCategoryFilter.selectors';
import { getCategoryItems, getFilterStateForOneValue } from 'component/page/search/SortAndFilter/Filter/filter.utils';
import { FilterCode } from 'gql/autogen/FilterCode';
import { updateCategoryQueryString } from 'component/page/search/SortAndFilter/sortAndFilterQuery.utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { StringFormatter } from '@agoda/react-handyman';
import { FilterState } from 'component/common/Filter/Filter.types';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';

const QuickCategoryFilterPlaceholder: React.FC<{ isDesktop: boolean; showPillShapedCategories: boolean; cms: CmsValue }> = (props) => {
    const width = !props.isDesktop || props.showPillShapedCategories ? '100px' : '200px';
    const height = !props.isDesktop || props.showPillShapedCategories ? '36px' : '104px';

    return (
        <Box display="flex">
            {[...Array<number>(7)].map((_, i) => (
                <Card key={i} me={!props.isDesktop ? '24' : '12'} width={width} height={height} overflow="hidden" variant="outlined">
                    <SkeletonLoader label={props.cms[326495]} shape="rectangle" width="100%" height="100%" />
                </Card>
            ))}
        </Box>
    );
};
QuickCategoryFilterPlaceholder.displayName = 'QuickCategoryFilterPlaceholder';

interface CategoryCardPropsWrapper {
    assetId: IconId;
    image: string;
    title: string;
    value: string;
    index: number;
    selectedCategory: string;
    onCategoryClick: (index: number) => void;
    showPillShapedCategories: boolean;
}

type CategoryCardProps = CategoryCardPropsWrapper;

const CategoryCard: React.FC<CategoryCardProps> = (props) => {
    const onClick = () => props.onCategoryClick(props.index);
    const onKeyDown = () => {
        onClick();
    };

    if (props.index === 0) {
        // Opt-out "All" Category Card/Chip
        return null;
    }

    if (props.showPillShapedCategories) {
        return (
            <Chip
                key={props.title}
                leadingIcon={props.assetId}
                label={props.title}
                onClick={onClick}
                selection={props.value === props.selectedCategory && props.index > 0 ? 'selected' : 'unselected'}
                data-testid="activities-quick-category-filter-chip-item"
                data-element-index={props.index}
                me="8"
            />
        );
    }

    return (
        // eslint-disable-next-line @agoda/agoda-a11y/no-inconsistent-patterns, @agoda/agoda-a11y/no-incorrect-props, @agoda/agoda-a11y/no-incorrect-props -- @agoda/agoda-a11y/no-inconsistent-patterns[541ba148-6384-45f1-820a-745249c6e5ca], @agoda/agoda-a11y/no-incorrect-props[5add9e0f-9124-43a2-b7aa-8d6dc52c8dc8], @agoda/agoda-a11y/no-incorrect-props[559f7a70-aaf5-4455-9651-85e4e481c592]
        <Card
            data-element-name="activities-quick-category-filter-item"
            data-testid="activities-quick-category-filter-item"
            data-element-index={props.index}
            me="12"
            p="12"
            alignItems="center"
            width="200px"
            minWidth="200px"
            type="interactive"
            status={props.value === props.selectedCategory && props.index > 0 ? 'selected' : undefined}
            variant="outlined"
            display="flex"
            flexDirection="column"
            onClick={onClick}
            onKeyDown={onKeyDown}
            tabIndex={0}
            overflow="hidden"
            textOverflow="ellipsis"
            role="button"
            aria-label={props.title}
        >
            <ActivityImage height="48px" width="48px" objectFit="cover" src={getImageUrl(props.image)} alt={props.title} mb="12" />
            <Typography variant="body-default-strong" textColor="base.subtle" textAlign="center" lineClamp={1}>
                {props.title}
            </Typography>
        </Card>
    );
};
CategoryCard.displayName = 'CategoryCard';

const QuickCategoryFilter: React.FC<Props> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const location = useLocation();
    const locationSearch = location.search;
    const locationHash = location.hash;
    const navigation = useNavigate();
    const [currentFilter, setCurrentFilters] = React.useState(props.filter);
    const categoryFilter = getCategoryItems(cms, currentFilter.valueFilterMatrix, props.filterMatrix?.valueFilterMatrix);
    const showPillShapedCategories = props.config.behavior.city.showPillShapedCategories || !props.screenSize.desktop;

    React.useEffect(() => {
        setCurrentFilters(props.filter);
    }, [props.filter]);

    const applyCategories = (categoryState: FilterState, index: number) => {
        const query = updateCategoryQueryString(locationSearch, locationHash, categoryState);
        setCurrentFilters(categoryState);
        props.setCategoriesFilter(props.sort, categoryState, index, categoryFilter[index].count);
        navigation(`?${query}`);
    };

    const onCategoryClick = (index: number) => {
        if (index === 0 || categoryFilter[index].selected) {
            const emptyState = getFilterStateForOneValue(FilterCode.Category, [], currentFilter);
            applyCategories(emptyState, index);
        } else {
            const selectedCategory = categoryFilter[index].values[0] as string;
            const categoryState = getFilterStateForOneValue(FilterCode.Category, [selectedCategory], currentFilter);
            applyCategories(categoryState, index);
        }
    };

    const categoryCards = categoryFilter.map((cat, index) => (
        <div key={cat.label}>
            <CategoryCard
                assetId={cat.asset.id}
                image={cat.asset.url}
                title={cat.label}
                value={cat.values[0]}
                index={index}
                selectedCategory={props.selectedCategory || ''}
                onCategoryClick={onCategoryClick}
                showPillShapedCategories={showPillShapedCategories}
            />
        </div>
    ));

    return (
        <Box backgroundColor="base.background" display="flex" flexDirection="column">
            <ContentContainer
                display="flex"
                flexDirection="column"
                flex={1}
                data-testid="activities-quick-category-filter-container"
                width="100%"
                mx="auto"
                py={props.screenSize.desktop ? '12' : '4'}
                enforceDefaultSpacing={true}
                maxWidth="100%"
            >
                <Box mt={props.screenSize.desktop ? '0' : props.isPageContainerSpacing ? undefined : '16'}>
                    <Typography variant="heading-l" id="activity-list" px="4">
                        {StringFormatter.formatToJsx(cms[298605], props.cityName)}
                    </Typography>
                </Box>
                <HorizontalScroll
                    display="flex"
                    pt={props.isPageContainerSpacing ? (props.screenSize.desktop ? '24' : '16') : undefined}
                    px={props.isPageContainerSpacing ? undefined : '4'}
                    py={props.isPageContainerSpacing ? undefined : '12'}
                    nextLabel={cms[130706]}
                    previousLabel={cms[130765]}
                >
                    {categoryCards.length === 0 ? (
                        <QuickCategoryFilterPlaceholder cms={cms} isDesktop={props.screenSize.desktop} showPillShapedCategories={showPillShapedCategories} />
                    ) : (
                        categoryCards
                    )}
                </HorizontalScroll>
            </ContentContainer>
        </Box>
    );
};
QuickCategoryFilter.displayName = 'QuickCategoryFilter';

export default connect(mapStateToProps, mapDispatchToProps)(QuickCategoryFilter);
